import { FilePen } from 'lucide-react'
import { Pie, PieChart } from 'recharts'

import EmptyCard from './EmptyCard'

import type { ImpactResponse } from '@/api/teamInsights'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart'
import { formatTooltipNumber } from '@/lib/utils'

const SLICE_COLORS = ['var(--chart-2)', 'var(--chart-1)']

const CATEGORY_LABELS: Record<string, string> = {
  clean_up: 'Clean up',
  dictionary_snippets: 'Dictionary & Snippets',
}

const prettifyCategory = (category: string): string =>
  CATEGORY_LABELS[category] ??
  category
    .split('_')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ')

interface AutoFormattingChartProps {
  response: ImpactResponse['auto_formatting']
}

const AutoFormattingChart = ({ response }: AutoFormattingChartProps) => {
  if (response.total === 0 || response.by_category.length === 0) {
    return (
      <EmptyCard
        icon={<FilePen className="size-5" />}
        title="Auto formatting"
        description="Insights will appear once we have enough data to calculate this trend"
      />
    )
  }

  const chartData = response.by_category.map((slice, i) => ({
    key: slice.category,
    label: prettifyCategory(slice.category),
    count: slice.count,
    fill: SLICE_COLORS[i % SLICE_COLORS.length],
  }))

  const chartConfig: ChartConfig = {
    count: { label: 'Changes' },
    ...Object.fromEntries(chartData.map((slice) => [slice.key, { label: slice.label, color: slice.fill }])),
  }

  return (
    <Card className="py-0 gap-0">
      <CardHeader className="flex items-center gap-4 space-y-0 border-b pl-6 pr-3 !py-3 sm:flex-row">
        <div className="flex flex-1 flex-col gap-0.5">
          <CardTitle className="text-xl font-semibold">
            {response.total.toLocaleString()} automatic fixes by Flow
          </CardTitle>
          <p className="text-[15px] leading-5 text-muted-foreground">
            Punctuation, formatting, and other fixes Flow applies automatically
          </p>
        </div>
      </CardHeader>
      <CardContent className="h-[280px] px-6 py-4">
        <ChartContainer config={chartConfig} className="h-full w-full">
          <PieChart>
            <ChartTooltip
              cursor={false}
              content={
                <ChartTooltipContent
                  className="w-fit max-w-none whitespace-nowrap"
                  hideLabel
                  formatter={(value, _name, item) => (
                    <div className="flex w-full items-center justify-between gap-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <span className="size-2.5 shrink-0 rounded-[2px]" style={{ background: item.payload?.fill }} />
                        <span className="text-muted-foreground">
                          {(item.payload as { label?: string } | undefined)?.label}
                        </span>
                      </div>
                      <span className="font-semibold text-foreground">{formatTooltipNumber(Number(value))}</span>
                    </div>
                  )}
                />
              }
            />
            <Pie data={chartData} dataKey="count" nameKey="key" innerRadius={0} strokeWidth={2} />
            <ChartLegend verticalAlign="bottom" content={<ChartLegendContent nameKey="key" />} />
          </PieChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

export default AutoFormattingChart
